
import React from 'react';

// Import PBS Revision Control types
import type { PbsRevisionLevel, PbsItemRevision, PbsRevisionStage, PbsRevisionStatus, PbsReviewStatus } from './types/pbsRevision';
export type { PbsRevisionLevel, PbsItemRevision, PbsRevisionStage, PbsRevisionStatus, PbsReviewStatus };

// Import ECN types
import type { ECNRecord, ApprovalRecord, PbsChangeSummary } from './pages/ECNManagement';
export type { ECNRecord, ApprovalRecord, PbsChangeSummary };

// ============================================
// Core DFR & FMEA Types
// ============================================

export enum RiskLevel {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
}

export interface IterationChange {
  changeId: string;
  category: 'Design' | 'Material' | 'FMEA' | 'Test' | 'Assembly' | 'Supplier';
  affectedItems: string[]; // BOM IDs, FMEA IDs, Material IDs
  description: string;
  rationale: string;
  initiatedBy: string; // User initials
  approvedBy: string; // Approver initials
  dateImplemented: string;
  costImpact?: number; // USD
  rpnImpact?: {
    before: number;
    after: number;
    reduction: number;
  };
}

export interface IterationRecord {
  iterationNumber?: number;
  epochNumber?: number;
  iteration?: number;
  iterationDate: string;
  iterationName?: string;
  epochName?: string;
  phase: 'Concept' | 'Design' | 'Prototype' | 'Validation' | 'Production';
  reviewers: string[]; // Array of reviewer initials
  changes: IterationChange[];
  fmeaSnapshot: {
    totalRPN: number;
    avgRPN: number;
    highRiskCount: number; // RPN >= 100
    criticalRiskCount: number; // S >= 9
    items?: FmeaItem[]; // Optional full list
  };
  bomSnapshot: {
    totalComponents: number;
    validatedComponents: number;
    totalCost: number;
    totalMass: number;
    items?: BomItem[]; // Optional full list
  };
  pDiagramSnapshot?: PDiagramData; // Legacy support
  pDiagramList?: PDiagramData[]; // SUPPORT FOR PLURAL P-DIAGRAMS
  testResults?: {
    testsCompleted: string[];
    testsFailed: string[];
    testsPassed: string[];
  };
  notes: string;
  fmeaItems?: FmeaItem[];
  bomItems?: BomItem[];
  
  // New SIPOC Metadata (v2.0.0)
  requirementChain?: any[];
  opalInsight?: any;
  derivedFunctions?: any[];
  auditReportMd?: string; // QUALITY AUDIT REPORT
  systemPiScore?: number; // RELIABILITY SCORE (Π)
  performanceMetrics?: any[];
  cadqueryScript?: string;
  shapes?: any[];
  contacts?: any[];

  nextActions: string[];
  projectName?: string;
  epochId?: number;
}

export interface ChangeRecord {
  timestamp: string;
  user: string;
  field: string;
  oldValue: string | number;
  newValue: string | number;
  comment: string;
}

export interface FmeaItem {
  id: string;
  function: string;
  itemFunction: string;
  requirement: string;
  failureMode: string;
  effects: string;
  S: number;
  O: number;
  D: number;
  RPN: number;
  actionPriority?: 'H' | 'M' | 'L';
  preventionControls: string[];
  detectionControls: string[];
  changeHistory?: ChangeRecord[];
  status: 'draft' | 'active' | 'archived' | 'superseded';  // REQUIRED (Metadata Standards v2.0.0)
  notes?: string;
  projectName?: string;
  epochId?: number;
  linkedBomIds?: number[];
  linkedCustomerReqIds?: string[];
  linkedProductFeatureIds?: string[];
  parentItemId?: string;
  refinementLevel?: number;
  specialCharacteristic?: 'CC' | 'SC' | 'none';  // AIAG-VDA: CC=Critical, SC=Significant

  // Action Tracking (AIAG-VDA Step 6)
  recommendedAction?: string;
  actionResponsibility?: string;
  actionTargetDate?: string;
  actionTaken?: string;
  actionCompletionDate?: string;
}

export type EditableFmeaRow = Omit<FmeaItem, 'preventionControls' | 'detectionControls' | 'RPN'> & {
  prevControls: string;
  detControls: string;
};


export interface ApprovalData {
  initials: string;
  note: string;
  timestamp: string;
}

export interface PDiagramData {
  systemName: string;
  consolidatedFunctionId: string;
  goal: string[];
  inputSignals: string[];
  controlFactors: string[];
  systemFunction: string[];
  expectedOutput: string[];
  unexpectedOutput: string[];
  noiseFactors: {
    pieceToPiece: string[];
    changeOverTime: string[];
    customerUsage: string[];
    externalEnvironment: string[];
    internal: string[];
  };
  status?: 'active' | 'draft' | 'inactive';
  approval?: ApprovalData;
  inactivation?: {
    timestamp: string;
    initials: string;
    reason: string;
  };

  // Metadata Standards (v2.0.0)
  epochId?: number;           // Which epoch generated this
  projectName?: string;       // Denormalized for fast filtering
}

export interface DvprTest {
  traceableRiskId: string[];
  testId: string;
  functionName: string;
  testName: string;
  procedure: string;
  criteria: string;
  testVariable?: string;      // e.g. "Max Temperature"
  testValue?: string;         // e.g. "62"
  testUnit?: string;          // e.g. "°C"
  testResult?: 'PASS' | 'FAIL' | 'MARGINAL';
  epochId?: number;
  projectName?: string;
  status?: 'planned' | 'in-progress' | 'completed' | 'failed' | 'cancelled';
  linkedCustomerReqIds?: string[];
}

export interface Control {
  id: string;
  type: 'prevention' | 'detection';
  name: string;
  description?: string;
  bomIds: number[];
  keywords?: string[];
  epochId?: number;
  projectName?: string;
  status?: 'draft' | 'active' | 'archived';
  effectiveness?: 'High' | 'Medium' | 'Low';
  implementationDate?: string;
  verifiedBy?: string;
  // Specific fields usually found in seed data
  testStandard?: string;
  acceptanceCriteria?: string;
  inspectionFrequency?: string;
  pbsLevel?: 'System' | 'Subsystem' | 'Component';
}

// ============================================
// New, Richer Data Types from Consolidated Mock
// ============================================

export interface SalesGroup {
  id: string;
  name: string;
  region: string;
}

export interface Country {
  id: string;
  name: string;
  salesGroupId: string;
  marketSegment: string;
}

export interface Customer {
  id: string;
  name: string;
  countryId: string;
  industry: string;
  tier: string;

  // Metadata Standards v2.0.0
  projectName?: string;
  epochId?: number;
}

export interface CustomerRequirement {
  id: string;
  customerId: string;
  requirementText: string;
  type: 'Performance' | 'Safety' | 'Usability' | 'Durability' | 'Regulatory';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  metric?: string;
  target?: string;
  linkedProductFeatureIds: string[];

  // Metadata Standards v2.0.0
  projectName?: string;       // Denormalized for fast filtering
  epochId?: number;           // Which epoch generated this
}

export interface ProductLine {
  id: string;
  name: string;
  description: string;
  market: string;
}

export interface Product {
  id: string;
  productLineId: string;
  name: string;
  description: string;
  partNumber: string;
  status: 'Concept' | 'Development' | 'Validation' | 'Production' | 'EOL';
  features: string[];
  linkedCustomerReqIds: string[];

  // Metadata Standards v2.0.0
  projectName?: string;
  epochId?: number;
}

export interface ProductFeature {
  id: string;
  productId: string;
  name: string;
  description: string;
  linkedCustomerReqIds: string[];
  linkedBomItemIds: number[];

  // Metadata Standards v2.0.0
  projectName?: string;
  epochId?: number;
}

export interface BomItem {
  id: number;
  level?: number;
  parentId?: number | null;
  childIds?: number[];
  component: string;
  partNumber?: string;
  description: string;
  basicFunction: string;
  quantity?: number;
  material?: string;
  materialId?: string;
  manufacturingProcess?: string;
  supplier?: string;
  isValidated?: boolean;
  validationStatus?: 'Not Started' | 'In Progress' | 'Passed' | 'Failed' | 'N/A';
  validationDate?: string;
  linkedFmeaIds?: string[];
  linkedTestIds?: string[];
  linkedMaterialSpecIds?: string[];
  criticality?: 'Critical' | 'High' | 'Medium' | 'Low';
  keywords?: string[]; // Added keywords as optional to match legacy Control usage if needed, though Control has bomIds
  epochId?: number;
  projectName?: string;
  status?: 'draft' | 'active' | 'archived' | 'superseded';
}

export interface MaterialSpecification {
  id: string;
  materialName: string;
  grade?: string;
  standard?: string;
  properties: {
    [key: string]: string | undefined;
  };
  linkedBomIds?: number[];
  supplier?: string;
  certificationRequired?: boolean;

  // Metadata Standards v2.0.0
  projectName?: string;
  epochId?: number;
}

export interface TestSpecification {
  id: string;
  name: string;
  standard?: string;
  description?: string;
  [key: string]: any;
}

export interface TestResult {
  id: string;
  testId?: string;
  result?: string;
  date?: string;
  [key: string]: any;
}

export interface TestMethod {
  id: string;
  name: string;
  standard: string;
  description: string;
  testType: 'Structural' | 'Environmental' | 'Performance' | 'Endurance' | 'Functional' | 'Safety';
  equipment: string[];
  linkedFmeaIds: string[];
  linkedBomIds: number[];
}

export interface TestReport {
  id: string;
  testMethodId: string;
  testDate: string;
  status: 'Pass' | 'Fail' | 'Conditional' | 'In Progress';
  linkedBomIds: number[];
  linkedFmeaIds: string[];
  reportUrl?: string;
  findings: string;
  recommendations?: string;
}

export interface TestStand {
  id: string;
  name: string;
  type: 'Mechanical' | 'Environmental' | 'Electrical' | 'Inspection';
  capacity: string;
  location: string;
  calibrationDate: string;
  nextCalibrationDue: string;
  status: 'Available' | 'In Use' | 'Maintenance' | 'Calibration';
}

export interface TestDataEntry {
  id: string;
  testMethodId: string;
  testStandId: string;
  bomItemIds: number[];
  testDate: string;
  operator: string;
  sampleId: string;
  stressLevel: string;
  failureTime?: number;
  failureMode?: string;
  censored: boolean;
  notes?: string;
}

export interface WeibullAnalysis {
  id: string;
  testMethodId: string;
  dataSetId: string;
  beta: number;
  eta: number;
  confidenceLevel: number;
  betaLower: number;
  betaUpper: number;
  etaLower: number;
  etaUpper: number;
  rSquared: number;
  b10Life: number;
  b50Life: number;
  analysisDate: string;
}

export interface AccelerationModel {
  id: string;
  name: string;
  type: 'Arrhenius' | 'Coffin-Manson' | 'Eyring' | 'Temperature-Humidity' | 'Power-Law';
  parameters: {
    [key: string]: number;
  };
  stressUnits: string;
  description: string;
}

export interface ReliabilityPrediction {
  id: string;
  bomItemId: number;
  testMethodId: string;
  weibullAnalysisId: string;
  accelerationModelId: string;
  useConditionStress: string;
  testConditionStress: string;
  accelerationFactor: number;
  predictedB10Life: number;
  predictedB50Life: number;
  confidenceLevel: number;
  predictionDate: string;
}

// ============================================
// AI / Chatbot Types
// ============================================

export interface PromptRevision {
  id: string;
  content: string;
  timestamp: string;
  author: string;
  version: number;
  notes?: string;
}

export interface BotConfig {
  id: string;
  name: string;
  description: string;
  role: string; // The "system" role description or capability
  activeRevisionId: string;
  revisions: PromptRevision[];

  // Metadata Standards v2.0.0
  projectName?: string;
  epochId?: number;
}


// ============================================
// App Context & Navigation Types
// ============================================

// --- Project / Unified Context Types ---

// Project Type Classification
export type ProjectType =
  | 'Production'      // Shipping product
  | 'Prototype'       // Pre-production validation
  | 'Experimental'    // R&D / POC
  | 'Platform'        // Shared technology across products
  | 'Derivative'      // Variant of existing product
  | 'CustomerSpecific'; // Custom project for specific customer

export type DevelopmentPhase =
  | 'Concept'         // Early ideation
  | 'Design'          // Active engineering
  | 'Validation'      // DVT/PVT testing
  | 'Production'      // Mass production
  | 'Sustaining'      // Post-launch support
  | 'EOL';            // End of life

export type FmeaStandard =
  | 'AIAG_V4'
  | 'AIAG_VDA'
  | 'SAE_J1739'
  | 'ISO_14971'
  | 'ISO_26262'         // Automotive Functional Safety
  | 'IEC_61508'         // Industrial Functional Safety
  | 'MIL_STD_1629A'
  | 'CUSTOMER_CUSTOM';

export interface ChangeLogEntry {
  timestamp: string;
  user: string;
  changeType: 'created' | 'updated' | 'merged' | 'migrated';
  description: string;
  fromVersion?: string;
  toVersion?: string;
}

export interface ProjectMetadata {
  // Core Identity
  id: string;                    // UUID v4
  name: string;                  // MUST match filename (sanitized)
  displayName?: string;          // Human-readable, allows special chars
  description: string;

  // Timestamps
  created: string;               // ISO 8601 timestamp
  lastModified: string;          // ISO 8601 timestamp

  // Execution State (optional, tracked for last active epoch if applicable)
  epochId?: number;

  // Versioning
  version: string;               // Semver
  schemaVersion?: string;        // Schema version (default: "2.0.0")

  // Author/Attribution
  author: string;                // Original creator
  createdBy?: string;            // User ID or "system"
  modifiedBy?: string;           // User ID or "system"

  // Organizational Hierarchy
  customerId?: string;           // FK to Customer.id
  customer?: string;             // Customer name (denormalized for quick access)
  productLineId?: string;        // FK to ProductLine.id
  productId?: string;            // FK to Product.id

  // Classification
  projectType?: ProjectType;     // Type categorization
  developmentPhase?: DevelopmentPhase;  // Current stage
  stage?: 'concept' | 'design' | 'prototype' | 'production' | 'maintenance';  // Simplified stage
  isExperimental?: boolean;      // Flag for R&D projects
  status?: 'active' | 'inactive' | 'archived';  // Project status

  // Compliance & Market
  targetCertifications?: string[];  // e.g., ["ISO 26262", "AIAG-VDA"]
  marketSegment?: string;        // Target market
  fmeaStandard?: FmeaStandard;    // Standard governing this project
  customerFormTemplate?: string;  // Template ID for custom customer forms

  // Security
  classification?: 'Public' | 'Internal' | 'Confidential' | 'Restricted';

  // Flexible Categorization
  tags?: string[];               // Flexible tags
  notes?: string;                // Optional human-readable notes

  // Audit Trail
  changeLog?: ChangeLogEntry[];  // History of significant changes
}

export interface ProjectContextData {
  customerName: string;
  productName: string;
  productDescription: string;
  objectives: string[];
}

export interface DfrData {
  projectMetadata?: ProjectMetadata;
  projectContext?: ProjectContextData;
  fmeaItems: FmeaItem[];
  bomItems: BomItem[];
  pDiagramList: PDiagramData[];
  dvprTests: DvprTest[];
  controls: Control[];

  // PBS Revision Control (Metadata Standards v2.0.0)
  pbsRevisions?: PbsRevisionLevel[];  // Design maturity tracking
  pbsItems?: PbsItemRevision[];       // PBS items with revision data

  // Engineering Change Notices (Metadata Standards v2.0.0)
  ecnLog?: ECNRecord[];                // Formal change control records

  // New collections
  customers: Customer[];
  customerRequirements: CustomerRequirement[];
  products: Product[];
  productFeatures: ProductFeature[];
  botConfigs: BotConfig[];
  materials?: MaterialSpecification[]; // Materials database
  
  // SIPOC & Mechatronic Synthesis Details (v2.0.0)
  requirementChain?: any[];
  opalInsight?: any;
  derivedFunctions?: any[];
  auditReportMd?: string; // QUALITY AUDIT REPORT
  systemPiScore?: number; // RELIABILITY SCORE (Π)
  performanceMetrics?: any[];
  cadqueryScript?: string;
  shapes?: any[];
  contacts?: any[];

  // SIPOC V4 Pipeline Outputs (v4.0.0)
  requirement_trace_matrix?: {
    hr_sr_table?: { id: string; type?: string; text?: string; standard?: string; status?: string }[];
    parameter_conflicts?: { id?: string; description?: string; affected_parts?: string[]; criticality?: string }[];
    pi_assignments?: any[];
    summary?: string;
  };
  fault_trees?: { top_event: string; tree: { nodes: any[]; connections: any[] } }[];
  optimization_proposals?: { targetFmeaId?: string; proposedAction?: string; actionType?: string; rationale?: string; expectedNewS?: number; expectedNewO?: number; expectedNewD?: number }[];
  dvp_report?: {
    test_plan?: { test_id: string; fmea_item_id?: string; requirement_id?: string; component_id?: string; test_name?: string; test_type?: string; test_standard?: string; pass_criteria?: string; sample_size?: number; duration?: string; severity_if_fail?: string; test_phase?: string }[];
    coverage_summary?: { total_tests?: number; requirements_covered?: number; fmea_items_covered?: number; uncovered_requirements?: string[]; estimated_test_duration_days?: number };
  };
  electrical_topology?: {
    connector_manifest?: { id?: string; part_number?: string; pins?: any[]; shielding?: string; ip_rating?: string }[];
    harness_strategy?: string;
  };
  semantic_gap_snapshot?: {
    gap_energy?: number;
    n_requirements?: number;
    n_design_entities?: number;
    top_gaps?: { semantic_direction?: string; weight_pct?: number }[];
    requirement_satisfaction?: { requirement?: string; gap?: number }[];
    pca_projection?: any;
  };
  semantic_gap_trajectory?: { epoch: number; gap_energy: number; top_gaps?: any[]; n_requirements?: number; n_design_entities?: number }[];

  // Seed data - comprehensive tracking across 17 iterations
  ecnSummary?: any; // ECN statistics and summary
  iterationHistory?: IterationRecord[]; // Iteration 1-17 tracking with scoring changes
  iterationSummary?: any; // Iteration statistics
  testSpecifications?: TestSpecification[]; // LIMS test specifications
  testResults?: TestResult[]; // LIMS test results across iterations
  testSummary?: any; // Test statistics
}

export type HighlightableItem =
  | { type: 'fmea'; id: string }
  | { type: 'bom'; id: number }
  | { type: 'p-diagram'; id: string }
  | { type: 'dvpr'; id: string }
  | { type: 'control'; id: string }
  | { type: 'risk-matrix'; id: string };

export type NavigationItem = {
  path: string;
  highlight?: HighlightableItem;
}

export interface NavLinkItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  advanced?: boolean;
  adminOnly?: boolean;
  requiredRole?: string;
  itemType?: string;
  divider?: boolean;  // Visual separator in navigation
  external?: boolean; // Opens in new tab (for external apps like PFMEA)
  children?: NavLinkItem[];
}

export type Theme = 'light' | 'dark' | 'high-contrast';
export type ViewMode = 'simple' | 'advanced';

export interface User {
  uid: string; // ISO 27001 Unique Identifier
  username: string;
  email?: string; // Added for IAM
  displayName?: string; // Added for IAM
  role: 'admin' | 'user' | 'magnus' | 'autoGainAdmin' | 'clientAdmin' | 'projectManager' | 'engineer' | 'viewer' | 'pending'; // Added 'pending'
  isApproved?: boolean; // Gatekeeper flag
  permissions: string[];
  allowedApps?: string[]; // RBAC: 'dfmea', 'pfmea', 'resolve'
  organization?: string;
  password?: string;
  // ECN-specific roles
  ecnCanCreate?: boolean;  // Can create and edit ECNs
  ecnCanApprove?: boolean; // Can review and approve/reject ECNs
  initials?: string;       // User initials for ECN tracking
}

export interface UserWithStatus extends User {
  status?: 'active' | 'suspended';
  createdDate?: string;
  lastLogin?: string;
  loginCount?: number;
}

// Subscription & Matrix Types
export interface SubscriptionConfig {
  matrix: Record<string, Record<string, boolean>>; // planId -> featureId -> boolean
}

export interface AppContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  highlightedItem: HighlightableItem | null;
  navigate: (path: string, options?: { state?: any, highlight?: HighlightableItem }) => void;
  goBack: () => void;
  canGoBack: boolean;
  isHelpOpen: boolean;
  setHelpOpen: (isOpen: boolean) => void;
  allData: DfrData;
  masterFunctions: string[];
  functionColorMap: Record<string, string>;
  isLoading: boolean;
  uploadMockData: () => Promise<void>;
  updateFmeaData: (fmeaItems: FmeaItem[]) => Promise<void>;
  updateFmeaItem: (fmeaItem: FmeaItem) => Promise<void>;
  addPDiagram: (pDiagram: PDiagramData) => Promise<void>;
  deletePDiagram: (id: string, status: 'active' | 'draft') => Promise<void>;
  refreshData: () => Promise<void>;
  switchDataSource: (mode: 'firestore' | 'local') => void;
  currentUser: User | null;    // Backend-First Persistence State
  syncStatus: 'synced' | 'syncing' | 'error' | 'offline';
  lastSyncTime: Date | null;
  dataSourceMode: 'firestore' | 'local' | 'backend';

  // Helper Functions
  clearCache: () => void;
  loadMockDataToBackend: () => Promise<void>;

  // Auth
  users: User[];
  login: (username: string, password?: string) => User | null;
  logout: () => void;
  updateUserPermissions: (username: string, permissions: string[]) => Promise<void>;
  // New methods for bots
  updateBotConfig: (botConfig: BotConfig) => Promise<void>;
  addUser: (user: User) => Promise<void>;
  addEpoch: (epoch: any) => Promise<void>;
  deleteEpoch: (epochNumber: number) => Promise<void>;
  changePassword: (username: string, password: string) => Promise<void>;
  logAction: (type: 'login' | 'navigation' | 'feature_usage' | 'admin_action' | 'logout', details: string, path?: string) => Promise<void>;
  addCustomer: (customer: Customer) => Promise<void>;
  addCustomerRequirement: (requirement: CustomerRequirement) => Promise<void>;
  updateProjectContext: (metadata: ProjectMetadata, context: ProjectContextData) => Promise<void>;
  apiMode: 'direct' | 'backend';
  setApiMode: (mode: 'direct' | 'backend') => void;

  // Subscription Management
  subscriptionConfig: SubscriptionConfig | null;
  updateSubscriptionConfig: (config: SubscriptionConfig) => void;
  hasFeature: (featureId: string) => boolean;

  // Multi-Project Support
  activeProject: string;
  setActiveProject: (project: string) => void;
  importProjectData: (data: DfrData, replace?: boolean) => Promise<void>;
  projectList: { name: string; epochIds: number[] }[];
  backendProjects: string[];
  refreshBackendProjects: () => Promise<void>;

  // PBS Manager Pro
  createPbsRevision: (revision: PbsRevisionLevel) => Promise<void>;
  updatePbsItem: (item: PbsItemRevision) => Promise<void>;

  // BOM Management
  updateBomItem: (item: BomItem) => Promise<void>;
  deleteBomItem: (id: number) => Promise<void>;

  // ECN Management
  updateEcnLog: (ecnLog: ECNRecord[]) => Promise<void>;

  // Functional Contracts
  approveWorkflowStep: (stepId: string, notes?: string) => Promise<void>;
  approveRequirement: (reqId: string, notes?: string) => Promise<void>;
  exportProject: (format?: 'csv' | 'json' | 'pdf') => Promise<string>;
  logTestResult: (testId: string, result: 'PASS' | 'FAIL' | 'MARGINAL', measuredValue?: string, units?: string, notes?: string) => Promise<void>;
  createControl: (control: any) => Promise<void>;
  createDvprTest: (test: any) => Promise<void>;
  runDvprAiAnalysis: () => Promise<string>;

  // Products & Customers Management
  updateProduct: (product: Product) => Promise<void>;
  deleteProduct: (productId: string) => Promise<void>;
  updateProductFeature: (feature: ProductFeature) => Promise<void>;
  deleteProductFeature: (featureId: string) => Promise<void>;
  updateCustomer: (customer: Customer) => Promise<void>;
  deleteCustomer: (customerId: string) => Promise<void>;
  isFullScreen: boolean;
  setIsFullScreen: (isFullScreen: boolean) => void;
}

export type GraphNode = {
  id: string;
  group: string;
  label: string;
  type: 'Function' | 'FMEA' | 'PrevControl' | 'DetControl' | 'DVP&R';
  color: string;
};

export type GraphLink = {
  source: string;
  target: string;
  type: 'P-F' | 'F-C' | 'F-D';
};


// ============================================
// External types added
// ============================================

export enum TestStatus {
  PLANNED = 'Planned',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  FAILED = 'Failed',
  CANCELLED = 'Cancelled'
}

export interface DvprItem {
  id: string;
  requirement: string;
  specSection: string;
  testMethod: string;
  sampleSize: number;
  duration: string;
  status: TestStatus;
  startDate?: string;
  endDate?: string;
  failures: number;
  comments: string;
}

export interface AltDataPoint {
  id: string;
  stressLevel: number; // e.g., Temperature in C
  cyclesToFailure: number;
  censored: boolean; // true if suspended (didn't fail)
}

export interface AltModel {
  name: string;
  paramA: number; // Slope / Shape
  paramB: number; // Intercept / Scale
}

export interface AiInsight {
  type: 'risk' | 'summary' | 'fmea';
  content: string;
}

// --- LIMS Types ---

export enum LabRequestStatus {
  DRAFT = 'Draft',
  SUBMITTED = 'Submitted',
  UNDER_REVIEW = 'Under Review',
  APPROVED = 'Approved',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed', // Technician done
  CLOSED = 'Closed',       // QA Approved
  REJECTED = 'Rejected',
  CANCELLED = 'Cancelled'
}

export enum RigStatus {
  AVAILABLE = 'Available',
  IN_USE = 'In Use',
  MAINTENANCE = 'Maintenance',
  CALIBRATION_DUE = 'Calibration Due'
}

export interface LabRig {
  id: string;
  name: string;
  type: string; // e.g., "Thermal Chamber", "Vibration Table"
  status: RigStatus;
  calibrationDue: string; // ISO Date string
  location: string;
  currentTestId?: string;
  capabilities?: string[]; // e.g. ["-40C to 150C", "Humidity"]
}

export interface LabSample {
  id: string; // Barcode
  name: string;
  batch: string;
  status: 'Created' | 'Received' | 'In Testing' | 'Stored' | 'Disposed';
  location: string;
}

export interface AuditLogEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
}

export interface UserActivityLog {
  id: string;
  timestamp: string;
  username: string;
  activityType: 'login' | 'navigation' | 'feature_usage' | 'admin_action' | 'logout';
  details: string;
  path?: string;
}

export interface LabDeviation {
  id: string;
  timestamp: string;
  type: 'Minor' | 'Major' | 'Critical';
  description: string;
  loggedBy: string;
  capaId?: string; // Link to CAPA system
}

export interface LabResultApproval {
  approverName: string;
  role: string;
  timestamp: string;
  digitalHash: string; // Simulates e-signature hash
}

export interface LabDailyCheck {
  id: string;
  timestamp: string;
  technician: string;
  status: 'Nominal' | 'Warning' | 'Error';
  notes: string;
}

export interface LabTestRequest {
  id: string;
  projectCode?: string;
  customer?: string;
  partDetails?: {
    name: string;
    batch: string;
    dateCode: string;
    qty: number;
  };
  testName: string;
  requestor: string;
  priority: 'Normal' | 'High' | 'Expedite';
  status: LabRequestStatus;
  requestDate: string;
  dueDate: string;
  samples: string[]; // Sample IDs
  assignedRigId?: string;
  protocolId?: string; // Link to standard method
  testParameters?: Record<string, string | number>;
  qcGates: {
    calibrationChecked: boolean;
    samplesVerified: boolean;
    environmentChecked: boolean;
  };
  dailyChecks?: LabDailyCheck[];
  results?: {
    summary: string;
    pass: boolean;
    technician: string;
  };
  deviations?: LabDeviation[];
  approval?: LabResultApproval;
  auditLog?: AuditLogEntry[];
  retestOf?: string; // ID of the parent request if this is a retest
  rigType?: string; // Preferred rig type for auto-scheduling
}

// --- Admin / User Types ---

export type LabRole = 'Technician' | 'Manager' | 'Admin' | 'Engineer';
export type ShiftType = '1st Shift (Day)' | '2nd Shift (Eve)' | '3rd Shift (Night)' | 'Weekend';

export interface LabUser {
  id: string;
  name: string;
  email: string;
  role: LabRole;
  shift: ShiftType;
  status: 'Active' | 'On Leave' | 'Inactive';
  skills: string[]; // e.g. ["Vibration", "Thermal", "High Voltage"]
}

// --- Specification-Aware Task Tracking Types ---

export type TaskType = 'action' | 'gate' | 'review' | 'approval';
export type TaskPriority = 'high' | 'medium' | 'low';
export type TaskStatus = 'draft' | 'open' | 'in_progress' | 'pending_review' | 'closed';

export interface TaskComment {
  id: string;
  userId: string;
  username: string;
  content: string;
  timestamp: string;
}

export interface Task {
  taskId: string;
  projectId: string;
  projectName?: string;

  // Link to source FMEA item
  fmeaItemId?: string;
  fmeaItemRef?: {
    id: string;
    function: string;
    failureMode: string;
    actionPriority?: 'H' | 'M' | 'L';
    RPN?: number;
  };

  // Task details
  title: string;
  description: string;
  taskType: TaskType;
  priority: TaskPriority;
  status: TaskStatus;

  // Assignment
  assignedTo?: string;
  assignedBy?: string;

  // Dates
  dueDate?: string;
  createdAt: string;
  updatedAt?: string;
  closedAt?: string;

  // Evidence & comments
  evidence?: string[];
  comments?: TaskComment[];

  // Specification context
  fmeaStandard?: FmeaStandard;
  workflowStep?: number;
  workflowStepName?: string;

  // Audit
  createdBy: string;
  closedBy?: string;
}

// Draft tasks are auto-generated and need review before becoming active
export interface DraftTask extends Omit<Task, 'status'> {
  status: 'draft';
  suggestedReason: string;  // Why this task was auto-generated
  sourceType: 'high_risk' | 'missing_field' | 'gate_check' | 'control_gap';
  autoGenerated: true;
  reviewedBy?: string;
  reviewedAt?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
}

// ============================================
// RBAC - Role-Based Access Control Types
// ============================================

export type ClientPlan = 'starter' | 'professional' | 'enterprise';
export type UserRole = 'autoGainAdmin' | 'clientAdmin' | 'projectManager' | 'engineer' | 'viewer';

// Client (Tenant) - Organization using Magnus-Core
export interface Client {
  clientId: string;
  name: string;
  plan: ClientPlan;

  // Feature access based on plan
  enabledApps: string[];      // App paths enabled for this client
  maxUsers: number;
  maxProjects: number;

  // Branding
  logoUrl?: string;
  primaryColor?: string;

  // Billing
  billingEmail: string;
  subscriptionStatus: 'active' | 'trial' | 'suspended' | 'cancelled';
  trialEndsAt?: string;

  // AutoGain Admin notes
  autoGainNotes?: string;

  // Timestamps
  createdAt: string;
  updatedAt?: string;
}

// User Profile with RBAC - Links to Firebase Auth UID
export interface UserProfile {
  uid: string;                // Firebase Auth UID
  email: string;
  displayName: string;
  clientId: string;           // Which client org they belong to

  // Role hierarchy
  role: UserRole;

  // Access control
  projectAccess: string[];    // Project IDs they can access
  appAccess: string[];        // App paths they can access (subset of client.enabledApps)

  // Item-level permissions
  itemAccess: {
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canApprove: boolean;
    canExport: boolean;
  };

  // ECN-specific permissions (for engineering users)
  ecnPermissions?: {
    canCreate: boolean;
    canApprove: boolean;
    initials: string;
  };

  // Profile
  avatarUrl?: string;
  department?: string;
  title?: string;
  phone?: string;

  // Status
  status: 'active' | 'invited' | 'suspended';
  lastLoginAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt?: string;
  createdBy: string;          // UID of admin who created
}

// Plan feature matrix
export const PLAN_FEATURES: Record<ClientPlan, {
  apps: string[];
  maxUsers: number;
  maxProjects: number;
  auditRetentionDays: number;
  features: string[];
}> = {
  starter: {
    apps: ['/dashboard', '/fmeas', '/p-diagram', '/bom-functions', '/export'],
    maxUsers: 5,
    maxProjects: 3,
    auditRetentionDays: 90,
    features: ['Basic FMEA', 'P-Diagram', 'Reports']
  },
  professional: {
    apps: ['/dashboard', '/fmeas', '/p-diagram', '/bom-functions', '/export',
      '/workflow-gates', '/action-tracking', '/ai-assistant', '/controls-library',
      '/customer-requirements', '/dvpr', '/risk-analysis'],
    maxUsers: 25,
    maxProjects: 20,
    auditRetentionDays: 365,
    features: ['All Starter features', 'Workflow Gates', 'Action Tracking', 'AI Assistant', 'Controls Library']
  },
  enterprise: {
    apps: ['*'], // All apps
    maxUsers: -1, // Unlimited
    maxProjects: -1, // Unlimited
    auditRetentionDays: 2555, // 7 years for compliance
    features: ['All Professional features', 'PFMEA Portal', 'API Access', 'SSO/SAML', 'Custom Integrations', 'Priority Support']
  }
};

// Role permissions matrix
export const ROLE_PERMISSIONS: Record<UserRole, {
  description: string;
  canManageUsers: boolean;
  canManageProjects: boolean;
  canAccessAllProjects: boolean;
  canViewAuditLogs: boolean;
  canExportData: boolean;
  canApproveItems: boolean;
}> = {
  autoGainAdmin: {
    description: 'AutoGain Platform Administrator',
    canManageUsers: true,
    canManageProjects: true,
    canAccessAllProjects: true,
    canViewAuditLogs: true,
    canExportData: true,
    canApproveItems: true
  },
  clientAdmin: {
    description: 'Organization Administrator',
    canManageUsers: true,
    canManageProjects: true,
    canAccessAllProjects: true,
    canViewAuditLogs: true,
    canExportData: true,
    canApproveItems: true
  },
  projectManager: {
    description: 'Project Manager',
    canManageUsers: false,
    canManageProjects: true,
    canAccessAllProjects: false,
    canViewAuditLogs: true,
    canExportData: true,
    canApproveItems: true
  },
  engineer: {
    description: 'Engineering User',
    canManageUsers: false,
    canManageProjects: false,
    canAccessAllProjects: false,
    canViewAuditLogs: false,
    canExportData: true,
    canApproveItems: false
  },
  viewer: {
    description: 'Read-Only Viewer',
    canManageUsers: false,
    canManageProjects: false,
    canAccessAllProjects: false,
    canViewAuditLogs: false,
    canExportData: false,
    canApproveItems: false
  }
};

// Audit Log Entry
export interface AuditLogEntry {
  id: string;
  clientId: string;
  userId: string;
  userEmail: string;
  userName: string;

  // Action details
  actionType: 'login' | 'logout' | 'view' | 'create' | 'update' | 'delete' | 'export' | 'permission_change' | 'approval';
  resourceType: 'fmea' | 'project' | 'control' | 'dvpr' | 'user' | 'app' | 'client' | 'pdiagram' | 'bom';
  resourceId: string;
  resourceName?: string;

  // Change details (for updates)
  before?: Record<string, any>;
  after?: Record<string, any>;
  changedFields?: string[];

  // Request metadata
  ipAddress: string;
  userAgent: string;
  sessionId?: string;

  // Timestamp
  timestamp: string;

  // Additional context
  metadata?: Record<string, any>;
}

// Invitation for new users
export interface UserInvitation {
  id: string;
  clientId: string;
  email: string;
  role: UserRole;
  projectAccess: string[];
  appAccess: string[];

  invitedBy: string;       // UID of inviter
  invitedAt: string;
  expiresAt: string;

  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  acceptedAt?: string;
  acceptedUserId?: string; // UID of user who accepted
}


// ============================================
// Platform Analytics Data Contracts
// Matches backend Pydantic models in admin.py
// ============================================

export interface AnalyticsMetric {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  value: string;
  trend: string;
}

export interface AnalyticsSection {
  icon: string;
  color: string;
  metrics: AnalyticsMetric[];
}

export interface RateLimitAlert {
  id: string;
  name: string;
  threshold: number;
  unit: string;
  severity: 'warning' | 'critical';
  description: string;
  current: number;
  enabled: boolean;
}

export interface AnalyticsSummary {
  active_users_today: number;
  active_users_trend: string;
  ai_calls_today: number;
  ai_calls_trend: string;
  mrr: number;
  mrr_trend: string;
  tokens_today: number;
  tokens_trend: string;
}

export interface PlatformAnalyticsResponse {
  summary: AnalyticsSummary;
  sections: Record<string, AnalyticsSection>;
  rate_limits: RateLimitAlert[];
  last_updated: string;
}

export interface UpdateRateLimitRequest {
  id: string;
  threshold?: number;
  enabled?: boolean;
}

export interface UpdateAnalyticsToggleRequest {
  section: string;
  metric_id: string;
  enabled: boolean;
}

export interface RateLimitStatus {
  id: string;
  current: number;
  threshold: number;
  percentage: number;
  status: 'ok' | 'warning' | 'critical';
  enabled: boolean;
}

// ============================================================
// PROJECT CONFIGURATION & SCOPING (AIAG-VDA Step 1)
export interface ProjectScoping {
  intent: {
    goals: string[];
    scopeBoundaries: string;
    assumptions: string[];
    exclusions?: string[];
  };
  timing: {
    kickoffDate: Date;
    targetCompletionDate: Date;
    milestones: Array<{
      name: string;
      date: Date;
      status: 'pending' | 'in-progress' | 'completed';
    }>;
  };
  team: {
    lead: string;
    members: Array<{
      name: string;
      role: string;
      department: string;
      expertise: string;
    }>;
  };
  tools: {
    software: string[];
    templates: string[];
    standards: string[];
    references: string[];
  };
  tasks: Array<{
    id: string;
    name: string;
    owner: string;
    dueDate: Date;
    status: 'not-started' | 'in-progress' | 'completed' | 'blocked';
    dependencies?: string[];
  }>;
}

export interface ProjectScopingData extends Omit<ProjectScoping, 'timing' | 'tasks'> {
  timing: Omit<ProjectScoping['timing'], 'kickoffDate' | 'targetCompletionDate' | 'milestones'> & {
    kickoffDate: string;
    targetCompletionDate: string;
    milestones: Array<Omit<ProjectScoping['timing']['milestones'][0], 'date'> & { date: string }>;
  };
  tasks: Array<Omit<ProjectScoping['tasks'][0], 'dueDate'> & { dueDate: string }>;
}
